/**
 * CyberAgent Service — wraps /api/admin/ai/cyber-agent/ endpoints
 * Phase 4.8-B — WQ-200
 */

// === Types ===

export interface VulnerabilityScan {
  id: string;
  scan_type: string;
  scan_target: string;
  vulnerabilities_found: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  auto_patches_applied: number;
  scan_duration_seconds: number | null;
  scanned_at: string;
  created_at: string;
}

export interface ScanDetail extends VulnerabilityScan {
  results: Record<string, unknown>;
}

export interface SecurityPosture {
  id: string;
  posture_score: number;
  vulnerability_score: number;
  license_score: number;
  patch_currency_score: number;
  owasp_score: number;
  threat_model_score: number;
  open_critical: number;
  open_high: number;
  details: Record<string, unknown> | null;
  computed_at: string;
}

export interface AutoPatch {
  id: string;
  scan_id: string;
  package_name: string;
  package_manager: string;
  old_version: string;
  new_version: string;
  patch_type: string;
  cve_ids: string[];
  status: string;
  applied_at: string | null;
  applied_by: string | null;
  created_at: string;
}

export interface IntelSource {
  id: string;
  name: string;
  source_category: string;
  source_url: string;
  source_type: string;
  schedule_interval: string | null;
  last_checked_at: string | null;
  last_item_count: number;
  is_active: boolean;
  config: Record<string, unknown> | null;
  created_at: string;
}

export interface ThreatModel {
  id: string;
  surface_name: string;
  surface_type: string;
  // WQ-224: backend returns dict {spoofing: N, tampering: N, ...} indexed by STRIDE category
  stride_classification: Record<string, number>;
  endpoints_count: number;
  // WQ-224: backend returns dict keyed by threat/mitigation name, not array
  threats: Record<string, unknown>;
  mitigations: Record<string, unknown>;
  gap_analysis: Record<string, unknown> | null;
  last_assessed_at: string;
  created_at: string;
}

export interface CyberDashboard {
  posture: SecurityPosture;
  recent_scans: VulnerabilityScan[];
  recent_patches: AutoPatch[];
  intel_sources: number;
  open_vulnerabilities: number;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// === API Functions ===

export async function getDashboard(authFetch: FetchFn): Promise<CyberDashboard> {
  const res = await authFetch('/api/admin/ai/cyber-agent/dashboard');
  if (!res.ok) throw new Error(`Failed to get cyber dashboard: ${res.status}`);
  return res.json();
}

export async function scanVulnerabilities(authFetch: FetchFn): Promise<VulnerabilityScan> {
  const res = await authFetch('/api/admin/ai/cyber-agent/scan/vulnerabilities', { method: 'POST' });
  if (!res.ok) throw new Error(`Failed to trigger vulnerability scan: ${res.status}`);
  return res.json();
}

export async function scanLicenses(authFetch: FetchFn): Promise<VulnerabilityScan> {
  const res = await authFetch('/api/admin/ai/cyber-agent/scan/licenses', { method: 'POST' });
  if (!res.ok) throw new Error(`Failed to trigger license scan: ${res.status}`);
  return res.json();
}

export async function scanOWASP(authFetch: FetchFn): Promise<VulnerabilityScan> {
  const res = await authFetch('/api/admin/ai/cyber-agent/scan/owasp', { method: 'POST' });
  if (!res.ok) throw new Error(`Failed to trigger OWASP scan: ${res.status}`);
  return res.json();
}

export async function listScans(
  authFetch: FetchFn,
  params?: { scan_type?: string; limit?: number },
): Promise<VulnerabilityScan[]> {
  const qs = new URLSearchParams();
  if (params?.scan_type) qs.set('scan_type', params.scan_type);
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  const res = await authFetch(`/api/admin/ai/cyber-agent/scans${query ? '?' + query : ''}`);
  if (!res.ok) throw new Error(`Failed to list scans: ${res.status}`);
  return res.json();
}

export async function getScanDetail(authFetch: FetchFn, scanId: string): Promise<ScanDetail> {
  const res = await authFetch(`/api/admin/ai/cyber-agent/scans/${scanId}`);
  if (!res.ok) throw new Error(`Failed to get scan detail: ${res.status}`);
  return res.json();
}

export async function getPosture(authFetch: FetchFn): Promise<SecurityPosture> {
  const res = await authFetch('/api/admin/ai/cyber-agent/posture');
  if (!res.ok) throw new Error(`Failed to get security posture: ${res.status}`);
  return res.json();
}

export async function getPostureHistory(
  authFetch: FetchFn,
  params?: { days?: number },
): Promise<SecurityPosture[]> {
  const qs = params?.days ? `?days=${params.days}` : '';
  const res = await authFetch(`/api/admin/ai/cyber-agent/posture/history${qs}`);
  if (!res.ok) throw new Error(`Failed to get posture history: ${res.status}`);
  return res.json();
}

export async function listPatches(
  authFetch: FetchFn,
  params?: { status?: string; limit?: number },
): Promise<AutoPatch[]> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  const res = await authFetch(`/api/admin/ai/cyber-agent/patches${query ? '?' + query : ''}`);
  if (!res.ok) throw new Error(`Failed to list patches: ${res.status}`);
  return res.json();
}

export async function listIntelSources(authFetch: FetchFn): Promise<IntelSource[]> {
  const res = await authFetch('/api/admin/ai/cyber-agent/intel-sources');
  if (!res.ok) throw new Error(`Failed to list intel sources: ${res.status}`);
  return res.json();
}

export async function registerIntelSource(
  authFetch: FetchFn,
  body: { name: string; source_category: string; source_url: string; source_type: string },
): Promise<IntelSource> {
  const res = await authFetch('/api/admin/ai/cyber-agent/intel-sources', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to register intel source: ${res.status}`);
  return res.json();
}

export async function getThreatModel(authFetch: FetchFn): Promise<ThreatModel[]> {
  const res = await authFetch('/api/admin/ai/cyber-agent/threat-model');
  if (!res.ok) throw new Error(`Failed to get threat model: ${res.status}`);
  return res.json();
}
